import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import CommonNumber from "@/pages/CommonNumber";
import PreviousResult from "@/pages/PreviousResult";
import TeerCalendar from "@/pages/TeerCalendar";
import DreamNumber from "@/pages/DreamNumber";
import Analytics from "@/pages/Analytics";
import PredictTarget from "@/pages/PredictTarget";
import TargetNumber from "@/pages/TargetNumber";
import ResultChart from "@/pages/ResultChart";
import Deal from "@/pages/Deal";
import Blog from "@/pages/Blog";
import ReputedCounter from "@/pages/ReputedCounter";
import OnlineShopping from "@/pages/OnlineShopping";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import StaticInfo from "@/pages/StaticInfo";
import useSeo from "@/hooks/useSeo";
import { Toaster } from "@/components/ui/sonner";
import { MessageCircle, Send, Mail } from "lucide-react";
import { KEYWORD_LANDINGS } from "@/constants/keywordLandings";

const VARIANT_MAP = {
  home: Home,
  common: CommonNumber,
  previous: PreviousResult,
  dream: DreamNumber,
  target: TargetNumber,
};

function AdminRoute() {
  useSeo({
    title: "Admin — Shillong Morning Teer",
    description: "Admin dashboard for Shillong Morning Teer publishers.",
    path: "/admin",
    robots: "noindex, nofollow",
  });
  const { user, checking } = useAuth();
  if (checking)
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center text-slate-500">
        Checking session…
      </div>
    );
  if (!user) return <AdminLogin />;
  return <AdminDashboard />;
}

function ContactUsPage() {
  useSeo({
    title: "Contact Us | Official Morning Teer",
    description:
      "Contact the Shillong Morning Teer team via WhatsApp, Telegram or Email for corrections, feedback and partnership queries.",
    path: "/contact-us",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Contact Us", path: "/contact-us" },
    ],
  });
  const links = [
    { href: "https://wa.me/916009660070", label: "WhatsApp", sub: "+91 60096 60070", Icon: MessageCircle, tint: "bg-emerald-100 text-emerald-700" },
    { href: "mailto:shillongteermorning4758@gmail.com", label: "Email", sub: "shillongteermorning4758@gmail.com", Icon: Mail, tint: "bg-amber-100 text-amber-800" },
    { href: "https://t.me/MorningSundayTeer", label: "Telegram", sub: "Morning Teer Channel", Icon: Send, tint: "bg-indigo-100 text-indigo-700" },
  ];
  return (
    <StaticInfo title="Contact Us" subtitle="We usually reply within a few hours">
      <p className="mb-4">
        Reach out anytime for questions, corrections, feedback or partnership inquiries about
        Shillong Morning Teer. WhatsApp is the fastest way to get a reply.
      </p>
      <div className="grid grid-cols-1 gap-3 not-prose">
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            target={l.href.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50 transition"
          >
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${l.tint}`}>
              <l.Icon className="h-5 w-5" />
            </span>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {l.label}
              </div>
              <div className="text-sm font-semibold text-slate-900 break-all">{l.sub}</div>
            </div>
          </a>
        ))}
      </div>
    </StaticInfo>
  );
}

function AboutUsPage() {
  useSeo({
    title: "About Official Morning Teer",
    description:
      "About Shillong Morning Teer — a community-run portal that publishes Morning Teer FR and SR results, common numbers, target numbers and result charts every day.",
    path: "/about-us",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "About Us", path: "/about-us" },
    ],
  });
  return (
    <StaticInfo title="About Official Morning Teer" subtitle="Meghalaya's community-run teer information portal">
      <p>
        Shillong Morning Teer is a community-run portal that publishes the daily Morning Teer
        Result — including First Round (F/R) and Second Round (S/R) winning numbers, the Common
        Number, the Target Number, Previous Results and the Result Chart.
      </p>
      <p>
        We focus on speed, accuracy and clarity. The homepage is designed to be mobile-first so
        followers can check the number in a couple of taps. Historical data is preserved so
        readers can research trends without dependency on third-party sites.
      </p>
      <p>
        <strong>This site is not affiliated with any government body or official counter
        authority.</strong> All information is provided for study and informational purposes
        only. Please cross-check every number from a reliable source before making any decision.
      </p>
    </StaticInfo>
  );
}

function DisclaimerPage() {
  useSeo({
    title: "Disclaimer | Official Morning Teer",
    description:
      "Disclaimer for Shillong Morning Teer — this site publishes results for informational purposes only and does not guarantee any prediction.",
    path: "/disclaimer",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Disclaimer", path: "/disclaimer" },
    ],
  });
  return (
    <StaticInfo title="Disclaimer" subtitle="Please read before playing">
      <p>
        This website publishes Shillong Morning Teer results for informational purposes only. We
        are not an official counter, agent or organiser of the Teer game and we do not accept
        any form of bets or payments.
      </p>
      <p>
        All predictions, common numbers, target numbers, house digits, ending digits and dream
        number references are shared as <strong>study references only</strong> — they do not
        guarantee any outcome. Please cross-check the exact number from a reliable or official
        source before making any decision.
      </p>
      <p>
        Please play only where Teer is legally regulated in your state or region and always
        play responsibly. Users must be of legal age.
      </p>
    </StaticInfo>
  );
}

function PrivacyPolicyPage() {
  useSeo({
    title: "Privacy Policy | Official Morning Teer",
    description:
      "Privacy Policy of Shillong Morning Teer — how we handle analytics data, cookies and information you send us via WhatsApp, Telegram or email.",
    path: "/privacy-policy",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Privacy Policy", path: "/privacy-policy" },
    ],
  });
  return (
    <StaticInfo title="Privacy Policy" subtitle="How we handle your data">
      <p>
        We collect minimal analytics (Google Analytics) to understand which pages are being
        used and how we can improve them. We do not sell personal data. Cookies are used only
        for essential session management on the admin dashboard.
      </p>
      <p>
        When you reach out on WhatsApp, Telegram or Email, your contact information is used
        only to reply to your query and is never shared with third parties.
      </p>
    </StaticInfo>
  );
}

function TermsPage() {
  useSeo({
    title: "Terms & Conditions | Official Morning Teer",
    description:
      "Terms & Conditions for using officialmorningteer.com — content is provided for informational purposes only and does not guarantee any outcome.",
    path: "/terms",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Terms & Conditions", path: "/terms" },
    ],
  });
  return (
    <StaticInfo title="Terms & Conditions" subtitle="Please read carefully">
      <p>
        Shillong Morning Teer publishes daily result numbers and educational data for
        informational purposes only. Participation in any Teer counter is subject to local
        laws and regulations of your jurisdiction. Users must be of legal age.
      </p>
      <p>
        Predictions, common numbers, target numbers, dream numbers and analytics are
        historical or statistical references and do not guarantee outcomes. The publisher
        assumes no financial liability for any decisions made based on the content of this
        site.
      </p>
    </StaticInfo>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Layout>
            <Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />

              {/* Canonical SEO routes */}
              <Route path="/common-number" element={<CommonNumber />} />
              <Route path="/target-number" element={<TargetNumber />} />
              <Route path="/previous-results" element={<PreviousResult />} />
              <Route path="/result-chart" element={<ResultChart />} />
              <Route path="/teer-calendar" element={<TeerCalendar />} />
              <Route path="/dream-number" element={<DreamNumber />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/counter" element={<ReputedCounter />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/deal" element={<Deal />} />
              <Route path="/online-shopping" element={<OnlineShopping />} />

              {/* Legacy route aliases — kept so old links still work */}
              <Route path="/previous-result" element={<PreviousResult />} />
              <Route path="/predict-target" element={<PredictTarget />} />
              <Route path="/predict-number" element={<Analytics />} />
              <Route path="/reputed-counter" element={<ReputedCounter />} />

              {/* Static pages (SEO friendly URLs) */}
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/about" element={<AboutUsPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/disclaimer" element={<DisclaimerPage />} />
              <Route path="/terms" element={<TermsPage />} />

              {/* Keyword-targeted SEO landing pages — auto-generated from KEYWORD_LANDINGS.
                  Each URL renders the appropriate page (home / common / previous / dream /
                  target) with keyword-specific <title>, meta description and H1. */}
              {KEYWORD_LANDINGS.map(({ path, variant }) => {
                const Component = VARIANT_MAP[variant];
                if (!Component) return null;
                return <Route key={path} path={path} element={<Component />} />;
              })}

              <Route path="/admin" element={<AdminRoute />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </Layout>
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
